'use client';

import React from 'react';
import styles from './MoreSection.module.scss';

import { useInView } from 'react-intersection-observer';
import createAnimationArr from '@/src/lib/helpers/createAnimationArr';
import Wrapper from '@/src/components/ui/wrapper/Wrapper';
import NextImage from "@/src/components/ui/nextImage/NextImage";

const heading = 'More Ways to Style Your Store';
const excerpt =
    'Explore a variety of design options to showcase your brand and products in the best possible way.';

type MoreItemProps = {
    image: string;
    image2x: string;
    title: string;
    text: string;
};

type MoreBlockProps = {
    title: string;
    image: string;
    image2x: string;
    text: string;
};

const gridItems: MoreItemProps[] = [
    {
        image: '/images/themes/prompt/more/pic-1.webp',
        image2x: '/images/themes/prompt/more/pic-1@2x.webp',
        title: '2 Types of Navigation Bars',
        text: 'Pick the navigation that works for your store: a sleek horizontal bar for a clean, modern look, or a vertical sidebar for easy browsing of many products.',
    },
    {
        image: '/images/themes/prompt/more/pic-2.webp',
        image2x: '/images/themes/prompt/more/pic-2@2x.webp',
        title: 'Extensive Product Page',
        text: 'A comprehensive product template that maximizes sales by consolidating images, details, and upsell options into a clean, above-the-fold floating card.',
    },
];

const listBlocks: MoreBlockProps[] = [
    {
        image: '/images/themes/prompt/more/pic-3.webp',
        image2x: '/images/themes/prompt/more/pic-3@2x.webp',
        title: 'Additional Offers ',
        text: 'Highlight additional offers without clutter. Customers can access full details through elegant buttons while the main page stays clean.',
    },
    {
        image: '/images/themes/prompt/more/pic-4.webp',
        image2x: '/images/themes/prompt/more/pic-4@2x.webp',
        title: 'Promo Banners',
        text: 'Highlight special offers and limited-time deals with eye-catching banners that grab attention and boost sales without interrupting shopping.',
    },
    {
        image: '/images/themes/prompt/more/pic-5.webp',
        image2x: '/images/themes/prompt/more/pic-5@2x.webp',
        title: 'Smooth Animations',
        text: 'Add polished, seamless animations that guide shoppers’ attention and make your store feel interactive and modern.',
    },
    {
        image: '/images/themes/prompt/more/pic-6.webp',
        image2x: '/images/themes/prompt/more/pic-6@2x.webp',
        title: 'Easy Customization',
        text: 'Effortlessly personalize your store. Change layouts, colors, and fonts to reflect your brand’s unique style.',
    },
];

const MoreGridItem: React.FC<MoreItemProps> = ({ image, image2x, title, text }) => (
    <div className={styles.moreSection__item}>
        <div className={styles.moreSection__pic}>
            <img
                loading="lazy"
                srcSet={`${image} 1x, ${image2x} 2x`}
                src={image}
                alt={title}
            />
        </div>
        <div className={styles.moreSection__about}>
            <h3 className={styles.moreSection__title}>{title}</h3>
            <div className={styles.moreSection__desc}>{text}</div>
        </div>
    </div>
);

const MoreListBlock: React.FC<MoreBlockProps> = ({ title, text, image, image2x }) => (
    <div className={styles.moreSection__block}>
        <div className={styles.moreSection__pic}>
            <img
                loading="lazy"
                srcSet={`${image} 1x, ${image2x} 2x`}
                src={image}
                alt={title}
            />
        </div>
        <h4 className={styles.moreSection__title}>{title}</h4>
        <div className={styles.moreSection__desc}>{text}</div>
    </div>
);

const MoreSection = () => {

    const {ref: inViewRef2, inView: inView2} = useInView({
        triggerOnce: true,
        rootMargin: '-250px 0px 0px',
        threshold: 0,
    });

    return (
        <section className={styles.moreSection}>
            <Wrapper>
                <div className={styles.moreSection__head} ref={inViewRef2}>
                    <h2 className={`heading__2 ${styles.heading} ${inView2 && styles.animate__heading}`}>
                        {createAnimationArr(heading, styles.word__wrapper, 0, 0.01)}
                    </h2>
                    <p className={`excerpt ${styles.excerpt} ${inView2 && styles.animate__excerpt}`}>
                        {createAnimationArr(excerpt, styles.word__wrapper, 0.1, 0.01)}
                    </p>
                </div>

                <div className={styles.moreSection__grid}>
                    {gridItems.map((item, idx) => (
                        <MoreGridItem key={idx} {...item} />
                    ))}
                </div>

                <div className={styles.moreSection__list}>
                    {listBlocks.map((block, idx) => (
                        <MoreListBlock key={idx} {...block} />
                    ))}
                </div>
            </Wrapper>
        </section>
    );
};

export default MoreSection;
